import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, MenuItem, Stack, TextField } from '@mui/material';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../../components/CustomTableWithPagination/GlobalTable';
import { getRetentionLeads } from '../../../../redux/features/crmSlice';
import { retentionLeadsHeaderFn, RETENTION_STUDENT_STATUS_OPTIONS } from './utils/retentionLeadsTableHead';

const DEFAULT_SORT = { field: 'assigned_on', direction: 'desc' };

/**
 * The receiving CRO's side of My Workspace > Inactive CROs.
 *
 * Lists the students a CRM handed over as retention leads. The list is scoped
 * server side to the logged in user, so there is no CRO picker here - a CRO
 * only ever sees their own.
 */
export default function RetentionLeads() {
  const dispatch = useDispatch();

  const retentionLeads = useSelector((state) => state.crm.retentionLeads.data);
  const loading = useSelector((state) => state.crm.retentionLeads.loading);
  const meta = useSelector((state) => state.crm.retentionLeads.meta);

  const [status, setStatus] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: DEFAULT_SORT,
    frozenColumns: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(
    () => ({
      page: tableState?.page,
      limit: tableState?.pageSize,
      search: tableState?.search,
      status,
      //WHEN THE USER CLEARS A COLUMN SORT, FALL BACK TO LATEST ASSIGNED FIRST
      sortBy: tableState?.sort?.field || DEFAULT_SORT.field,
      sortOrder: tableState?.sort?.field ? tableState?.sort?.direction : DEFAULT_SORT.direction,
    }),
    [status, tableState?.page, tableState?.pageSize, tableState?.search, tableState?.sort],
  );

  const retentionLeadsHeader = useMemo(() => retentionLeadsHeaderFn(), []);

  useEffect(() => {
    const promise = dispatch(getRetentionLeads(dispatchParams));
    return () => promise?.abort?.();
  }, [dispatch, dispatchParams, refreshKey]);

  return (
    <GlobalWrapper title={'Retention Leads'}>
      <Stack gap={1}>
        <Box display={'flex'} gap={1} flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="Student Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setTableState((prev) => ({ ...prev, page: 1, checkedRowIds: [] }));
            }}
            sx={{ width: { xs: '100%', sm: 220 } }}
          >
            {RETENTION_STUDENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Chip color="primary" variant="outlined" label={`${meta?.totalRecords ?? 0} retention lead(s)`} />
        </Box>

        <GlobalTable
          rowUniqueId="retention_assignment_id"
          headers={retentionLeadsHeader}
          rows={retentionLeads}
          tableState={tableState}
          setTableState={setTableState}
          loading={loading}
          meta={meta}
          handleRefresh={() => setRefreshKey((prev) => prev + 1)}
        />
      </Stack>
    </GlobalWrapper>
  );
}
